import { useState } from "react";
import type { FormEvent } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Check, KeyRound, MailCheck } from "lucide-react";
import { API_BASE_URL } from "@/utils/env";
import { BRAND } from '@/brand';

const ACCENT = BRAND === 'corporate' ? '#E8890B' : '#1976D2';
const BRAND_NAME = BRAND === 'corporate' ? 'Run & Comply' : 'Grow & Raise';

function AdminForgotPassword() {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState(false);

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setError("");
        setSuccess(false);

        if (!email.trim()) { setError("Please enter your admin email address"); return; }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) { setError("Please enter a valid email address"); return; }

        setLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/auth/admin-forgot-password`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email }),
            });
            const data = await response.json();
            if (!response.ok || !data.success) throw new Error(data.error || "Failed to send reset email");
            setSuccess(true);
        } catch (err: any) {
            console.error("Admin forgot password error:", err);
            setError(err.message || "Failed to send reset email. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-[1fr_1fr]" style={{ fontFamily: "'Montserrat',system-ui,sans-serif" }}>
            {/* Left — form */}
            <div className="flex flex-col justify-center px-6 sm:px-12 lg:px-16 py-12 bg-white">
                <div className="max-w-md w-full mx-auto">
                    <Link to="/" className="flex items-center gap-2 mb-8">
                        <span className="w-9 h-9 rounded-xl grid place-items-center text-white" style={{ background: ACCENT }}><ShieldCheck className="w-5 h-5" /></span>
                        <div>
                            <div className="text-[15px] font-black text-slate-900 leading-none">Conncct <span style={{ color: ACCENT }}>Admin</span></div>
                            <div className="text-[10px] font-bold tracking-[0.14em] uppercase text-slate-400 mt-1">{BRAND_NAME}</div>
                        </div>
                    </Link>

                    {!success ? (
                        <>
                            <div className="w-12 h-12 rounded-2xl grid place-items-center mb-5" style={{ background: `${ACCENT}1a`, color: ACCENT }}><KeyRound className="w-6 h-6" /></div>
                            <h1 className="text-2xl font-black text-slate-900 tracking-tight">Reset your admin password</h1>
                            <p className="text-sm text-slate-500 mt-1.5 mb-7">We'll verify your admin access, then email you a secure link to set a new password.</p>

                            {error && <div className="mb-4 text-sm font-semibold text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}

                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div>
                                    <label className="block text-xs font-bold text-slate-600 mb-1.5">Admin email</label>
                                    <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@conncct.com" autoComplete="email" disabled={loading}
                                        className="w-full rounded-xl border border-slate-300 px-3.5 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-200 focus:border-slate-400" />
                                </div>
                                <button type="submit" disabled={loading} className="w-full rounded-xl py-3 text-sm font-bold text-white disabled:opacity-60" style={{ background: ACCENT }}>{loading ? "Verifying…" : "Send reset link"}</button>
                            </form>

                            <Link to="/admin-login" className="inline-block mt-6 text-sm font-semibold text-slate-500 hover:text-slate-800">← Back to admin login</Link>
                        </>
                    ) : (
                        <>
                            <div className="w-14 h-14 rounded-full grid place-items-center mb-5 bg-green-50 text-green-600"><MailCheck className="w-7 h-7" /></div>
                            <h1 className="text-2xl font-black text-slate-900 tracking-tight">Check your email</h1>
                            <p className="text-sm text-slate-500 mt-1.5">We verified your admin access and sent a reset link to <strong className="text-slate-800">{email}</strong>.</p>
                            <p className="text-xs text-slate-400 mt-3 mb-7">The link expires in 1 hour. If it's not in your inbox, check spam.</p>
                            <Link to="/admin-login" className="inline-block w-full text-center rounded-xl py-3 text-sm font-bold text-white" style={{ background: ACCENT }}>Return to admin login</Link>
                            <button onClick={() => { setSuccess(false); setEmail(""); }} className="mt-4 text-sm font-bold" style={{ color: ACCENT }}>Send another email</button>
                        </>
                    )}
                </div>
            </div>

            {/* Right — brand rail */}
            <div className="hidden lg:flex flex-col justify-center px-16 text-white relative overflow-hidden" style={{ background: 'linear-gradient(150deg, #17263A, #0E1A2B)' }}>
                <div aria-hidden style={{ position: 'absolute', width: 420, height: 420, borderRadius: '50%', top: -140, right: -120, background: `radial-gradient(circle, ${ACCENT}55, transparent 70%)`, filter: 'blur(14px)' }} />
                <div className="relative z-10 max-w-md">
                    <div className="text-[11px] font-bold tracking-[0.18em] uppercase" style={{ color: ACCENT === '#1976D2' ? '#8fc0f5' : '#F5B841' }}>Conncct Admin · {BRAND_NAME}</div>
                    <h2 className="text-3xl font-black tracking-tight mt-3 leading-tight">Secure access, always verified.</h2>
                    <p className="text-[15px] text-slate-300 mt-4 leading-relaxed">Password resets are gated on admin access and expire quickly, so the back office stays in your team's hands only.</p>
                    <ul className="mt-8 space-y-3">
                        {['Admin access verified before any reset', 'One-time link, expires in 60 minutes', 'Every reset is audit-logged'].map(t => (
                            <li key={t} className="flex items-center gap-3 text-sm text-slate-200"><span className="w-5 h-5 rounded-full grid place-items-center flex-none" style={{ background: ACCENT }}><Check className="w-3 h-3 text-white" /></span>{t}</li>
                        ))}
                    </ul>
                </div>
            </div>
        </div>
    );
}

export default AdminForgotPassword;
